/* The upsell handoff, client side.
 *
 * The offer that follows a purchase used to be requested with the raw Stripe
 * payment intent id, which meant anyone holding that id could ask for a charge on
 * the card that paid for the purchase — and the id travelled in a page URL. The
 * server now mints a short-lived signed token alongside the payment intent and
 * requires it to make the charge.
 *
 * Kept in sessionStorage rather than threaded through props: the token has to
 * survive a navigation from the checkout to /giveaway/upsell, it belongs to this
 * tab and this purchase only, and passing it down through five components on the
 * payment path would be a lot of new wiring on the one flow least able to afford a
 * mistake. Keyed by the payment intent so two purchases in one tab cannot collide.
 *
 * Losing it is survivable and not silent: the charge is refused and the buyer sees
 * the same "we couldn't complete this" they would see for an expired offer.
 */

const key = (paymentIntentId: string) => `incrypt.upsellToken:${paymentIntentId}`;

/* Returns whether the token was stored, so the caller can decline to OFFER an
 * upsell it would not be able to charge — during a deploy where the server does not
 * mint one yet, or in a browser that refuses sessionStorage. Showing the offer and
 * failing on the click is a worse experience than never showing it. */
export const rememberUpsellToken = (
  paymentIntentId?: string | null,
  token?: string | null
): boolean => {
  if (!paymentIntentId || !token) return false;
  try {
    sessionStorage.setItem(key(paymentIntentId), token);
    return true;
  } catch {
    /* private mode, storage disabled — the upsell simply will not be offered */
    return false;
  }
};

export const recallUpsellToken = (paymentIntentId?: string | null) => {
  if (!paymentIntentId) return null;
  try {
    return sessionStorage.getItem(key(paymentIntentId));
  } catch {
    return null;
  }
};

/* Once the offer is taken or declined the token has done its job. */
export const forgetUpsellToken = (paymentIntentId?: string | null) => {
  if (!paymentIntentId) return;
  try {
    sessionStorage.removeItem(key(paymentIntentId));
  } catch {
    /* nothing to do */
  }
};
